import { Loader2, Play, Target } from 'lucide-react'

interface EmptyStateProps {
  onRunAnalysis: () => void
}

export function EmptyState({ onRunAnalysis }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
      <div className="w-20 h-20 rounded-2xl bg-bg-secondary flex items-center justify-center mb-6">
        <Target className="w-10 h-10 text-fg" />
      </div>
      <h3
        className="m-0 mb-3 text-fg"
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 28,
          fontWeight: 600,
          letterSpacing: '-0.015em',
        }}
      >
        Start your validation journey
      </h3>
      <p className="text-fg-secondary max-w-lg mb-8" style={{ lineHeight: 1.55 }}>
        Skerpa will analyze your product intelligence to see where you stand on the
        validation ladder — from problem existence to product–market fit.
      </p>
      <button
        type="button"
        onClick={onRunAnalysis}
        className="inline-flex items-center gap-2.5 rounded-lg text-fg-inverse transition-colors hover:bg-[#14242e]"
        style={{
          background: 'var(--color-fg)',
          padding: '12px 22px',
          fontWeight: 500,
        }}
      >
        <Play className="w-4 h-4" />
        Run validation analysis
      </button>
    </div>
  )
}

interface RunningStateProps {
  progress: { stepsCompleted: number; totalSteps: number; currentStepName: string | null }
}

const RUNNING_STEP_NAMES = [
  'Gathering your product intelligence…',
  'Evaluating each validation stage…',
  'Drafting recommendations…',
]

export function RunningState({ progress }: RunningStateProps) {
  const currentStep = RUNNING_STEP_NAMES[progress.stepsCompleted] ?? 'Processing…'
  const total = Math.max(progress.totalSteps, 1)
  const fraction = Math.min(1, (progress.stepsCompleted + 1) / total)

  return (
    <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
      <div className="w-20 h-20 rounded-2xl bg-bg-secondary flex items-center justify-center mb-6">
        <Loader2 className="w-10 h-10 text-fg animate-spin" />
      </div>
      <h3
        className="m-0 mb-3 text-fg"
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 28,
          fontWeight: 600,
          letterSpacing: '-0.015em',
        }}
      >
        Analyzing your product
      </h3>
      <p className="text-fg-secondary max-w-lg mb-8">{currentStep}</p>
      <div
        className="w-80 h-1.5 rounded-full overflow-hidden"
        style={{ background: 'var(--color-border)' }}
      >
        <div
          className="h-full rounded-full transition-[width] duration-[var(--dur-slow)]"
          style={{
            width: `${fraction * 100}%`,
            background: 'var(--color-fg)',
          }}
        />
      </div>
      <p
        className="mt-3 text-fg-muted font-mono"
        style={{ fontSize: 11, letterSpacing: '0.06em' }}
      >
        Step {progress.stepsCompleted + 1} of {total}
      </p>
    </div>
  )
}

export function LoadingState() {
  return (
    <div className="p-10">
      <div className="animate-pulse space-y-6">
        <div
          className="h-10 rounded-lg w-72"
          style={{ background: 'var(--color-border)' }}
        />
        <div
          className="h-96 rounded-2xl"
          style={{ background: 'var(--color-bg-secondary)' }}
        />
      </div>
    </div>
  )
}
